import { signOut } from 'firebase/auth'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser, faSignOutAlt, faBuilding, faExchangeAlt, faCog } from '@fortawesome/free-solid-svg-icons'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/frontend/contexts/AuthContext'
import { useCompany } from '@/frontend/contexts/CompanyContext'
import { auth } from '@/frontend/lib/firebase'

/**
 * Custom UserButton component with Firebase authentication
 * Replaces Clerk's UserButton with custom implementation
 */
export default function UserButton() {
	const { user, isLoaded } = useAuth()
	const { selectedCompany, availableCompanies } = useCompany()
	const navigate = useNavigate()

	// Show loading state while user data is loading
	if (!isLoaded) {
		return (
			<Avatar className="h-8 w-8">
				<AvatarFallback>
					<FontAwesomeIcon icon={faUser} className="h-4 w-4" />
				</AvatarFallback>
			</Avatar>
		)
	}

	// If no user is loaded, don't show anything
	if (!user) {
		return null
	}

	const handleSignOut = async () => {
		try {
			await signOut(auth)
			console.log('[Auth] User signed out successfully')
		} catch (error) {
			console.error('[Auth] Sign out error:', error)
		}
	}

	const handleSwitchCompany = () => {
		navigate('/select-company?switching=true')
	}

	const handleSettings = () => {
		navigate('/settings/profile')
	}

	const displayName = user.displayName || user.email?.split('@')[0] || 'User'
	const userInitials = displayName
		.split(' ')
		.map(name => name[0])
		.join('')
		.toUpperCase()
		.slice(0, 2)

	return (
		<div className="flex items-center gap-2">
			<span className="text-white text-sm hidden sm:block">
				{displayName}
			</span>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Avatar className="h-8 w-8 cursor-pointer">
						{user.photoURL ? (
							<AvatarImage src={user.photoURL} alt={displayName} />
						) : (
							<AvatarFallback className="bg-blue-600 text-white">
								{userInitials}
							</AvatarFallback>
						)}
					</Avatar>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="w-64">
					<DropdownMenuLabel>
						<div className="flex flex-col space-y-1">
							<p className="text-sm font-medium leading-none">{displayName}</p>
							<p className="text-xs leading-none text-muted-foreground">{user.email}</p>
						</div>
					</DropdownMenuLabel>
					
					{/* Current Company Section */}
					{selectedCompany && (
						<>
							<DropdownMenuSeparator />
							<DropdownMenuLabel>
								<div className="flex flex-col space-y-2">
									<p className="text-xs font-medium text-muted-foreground">Current Company</p>
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-2">
											<FontAwesomeIcon icon={faBuilding} className="h-3 w-3" />
											<span className="text-sm">{selectedCompany.company}</span>
										</div>
										<Badge variant="outline" className="text-xs">
											{selectedCompany.role}
										</Badge>
									</div>
								</div>
							</DropdownMenuLabel>
						</>
					)}
					
					<DropdownMenuSeparator />
					
					{/* Settings Option */}
					<DropdownMenuItem onClick={handleSettings} className="cursor-pointer">
						<FontAwesomeIcon icon={faCog} className="mr-2 h-4 w-4" />
						<span>Settings</span>
					</DropdownMenuItem>
					
					{/* Company Switching Option */}
					{availableCompanies.length > 1 && (
						<DropdownMenuItem onClick={handleSwitchCompany} className="cursor-pointer">
							<FontAwesomeIcon icon={faExchangeAlt} className="mr-2 h-4 w-4" />
							<span>Switch Company</span>
						</DropdownMenuItem>
					)}
					
					<DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
						<FontAwesomeIcon icon={faSignOutAlt} className="mr-2 h-4 w-4" />
						<span>Sign out</span>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	)
}
